import { useCallback } from "react";
import { useQueryClient } from "react-query";
import { useCache } from "../context/CacheContext";
import { collectionPrefixes } from "./queryKeys";

/**
 * Centralized cache-invalidation hook — the single, consistent way to refresh
 * application data after a write.
 *
 * Background (from the audit): invalidation was handled three different ways
 * across the app — `updateCacheKey()` (busts every Firestore query), ad-hoc
 * `queryClient.invalidateQueries(...)`, and manual `refetch()` — and several
 * write paths refreshed only one of the two order caches (Firestore vs API),
 * leaving the other stale.
 *
 * `useDataCache` unifies this:
 *  - `invalidateAll()` mirrors the legacy `updateCacheKey()` behavior (busts all
 *    Firestore-backed reads) AND invalidates the API-backed order/driver caches,
 *    so a single call guarantees a consistent refresh everywhere.
 *  - `invalidate(prefix)` targets a single dataset when a narrower refresh is
 *    enough.
 *
 * It is purely additive: existing `updateCacheKey()` call sites keep working.
 */
export const useDataCache = () => {
  const { updateCacheKey } = useCache();
  const queryClient = useQueryClient();

  /** Refresh every dataset (Firestore + API order/driver caches). */
  const invalidateAll = useCallback(() => {
    updateCacheKey();
    queryClient.invalidateQueries([collectionPrefixes.ordersApi]);
    queryClient.invalidateQueries([collectionPrefixes.invoicesApi]);
    queryClient.invalidateQueries([collectionPrefixes.driversApi]);
  }, [updateCacheKey, queryClient]);

  /** Refresh a single dataset by its collection prefix. */
  const invalidate = useCallback(
    (prefix: string) => {
      queryClient.invalidateQueries([prefix]);
    },
    [queryClient],
  );

  /**
   * Refresh the API-backed order caches after an order write. The Firestore
   * realtime orders (OrdersRealtimeProvider) need no invalidation — the live
   * onSnapshot listener picks up writes automatically. Rotating the cache key
   * here used to tear down and recreate that subscription on every write,
   * which was both wasteful and a source of stuck-loading states.
   */
  const invalidateOrders = useCallback(() => {
    queryClient.invalidateQueries([collectionPrefixes.ordersApi]);
    queryClient.invalidateQueries(["invoiceCandidates"]);
  }, [queryClient]);

  /**
   * Refresh the (API-backed) invoice list. With an infinite query this refetches
   * every page already loaded for the active key, keeping the list consistent
   * after invoice create/update/book/payment.
   */
  const invalidateInvoices = useCallback(() => {
    queryClient.invalidateQueries([collectionPrefixes.invoicesApi]);
    queryClient.invalidateQueries(["invoiceCandidates"]);
  }, [queryClient]);

  return { invalidateAll, invalidate, invalidateOrders, invalidateInvoices };
};
